import Button from '../../../button/Button';
import { ReactComponent as BusinessIcon } from 'assets/ui-update/business.svg';
import { ReactComponent as IndividualIcon } from 'assets/ui-update/individual.svg';
import { ReactComponent as DeleteIcon } from 'assets/ui-update/delete.svg';
import { ReactComponent as WarningIcon } from 'assets/ui-update/warning.svg';
import { useDivHeight } from '../../../../helpers/useDivHeight';
import { TreeActionButtons, TreeActionButtonsProps } from './TreeActionButtons';
import {
    BeneficialBusinessOwner,
    BeneficialPersonalOwner,
    FormValues,
    OwnerTreeStructure,
} from '../../schema';
import { SingleNewItem } from './AddNewOwner/SingleNewItem';
import { useField, useFormikContext } from 'formik';
import { ErrorM } from '../../../form/ErrorM';
import { beneficialOwnersStructureSchema } from '../../validation';
import {
    getDirectOwnersPercentageTotal,
    getParentTreeFieldname,
    useIsMainInstance,
} from './helpers';
import { useEffect, useRef, useState } from 'react';

const FAKE_ITEM_FOR_VALIDATION = {
    ownersGuid: 'FAKE_GUID',
    bIsBusiness: false,
    percentageOwned: 20,
    bControllingParty: true,
    positionAtCompany: 'positionAtCompany',
    children: null,
};

const hasErrorField = (errors?: Partial<OwnerTreeStructure>) => {
    return (
        !!errors?.bControllingParty ||
        !!errors?.ownersGuid ||
        !!errors?.percentageOwned ||
        !!errors?.positionAtCompany
    );
};

const MAX_DEPTH = 5;
export const isBusinessOwner = (
    _owner: BeneficialPersonalOwner | BeneficialBusinessOwner,
    bIsBusiness: boolean
): _owner is BeneficialBusinessOwner => bIsBusiness;

type SingleItemProps = {
    checkHasControllingParty: (nodes: any[]) => boolean;
    hasControllingParty?: boolean;
    item: OwnerTreeStructure;
    hideLeftBar: boolean; // hides the end of the branch after the circle
    hideBranchLink: boolean; // hodes the full stretch of the branch
    depth: number; // used as we set an arbitrary max depth
    fieldname: string;
    handleDeleteOwner: (fieldname: string, parentFieldName: string) => void;
    handleUpdateOwner: (fieldname: string) => void;
    lookupOwner: (
        guid: string,
        bIsBusiness: boolean
    ) => {
        owner: BeneficialPersonalOwner | BeneficialBusinessOwner | undefined;
        fieldname: string;
    };
} & Omit<TreeActionButtonsProps, 'parentFieldname' | 'hasError' | 'hasChildren'>;

export const SingleTreeItem: React.FC<SingleItemProps> = ({
    item,
    depth,
    fieldname,
    handleDeleteOwner,
    handleUpdateOwner,
    lookupOwner,
    hideLeftBar,
    hideBranchLink,
    hasControllingParty,
    checkHasControllingParty,
    ...actionProps
}) => {
    const [ref, height] = useDivHeight();
    const { submitCount } = useFormikContext<FormValues>();
    const [{ value }, { error: fieldError, touched }, { setValue }] =
        useField<OwnerTreeStructure>(fieldname);
    const { owner, fieldname: ownerInfoFieldname } = lookupOwner(item.ownersGuid, item.bIsBusiness);
    const [, { error: ownerInfoError }] = useField<
        BeneficialPersonalOwner | BeneficialBusinessOwner
    >(ownerInfoFieldname);

    // Main instance is always true for individuals and only true for business if it's the instance with children
    const isMainInstance = useIsMainInstance(fieldname);

    // hacky clean up from copying during deletion
    if (!isMainInstance && item.children) setValue({ ...value, children: null });

    /* crude check that it's a real validation error and not and error from the parent
    eg. parent error "We have an error" this fieldname is parent[0] so this error is "W" */
    const error = typeof fieldError === 'string' && fieldError.length > 1;
    const hasErrorInItem = !error ? hasErrorField(fieldError as any) : false;

    const hasSubmitted = submitCount > 0;
    const hasError = error && touched && typeof fieldError === 'string' && hasSubmitted;

    const parentFieldname = getParentTreeFieldname(fieldname);

    const itemForValidation = {
        ...item,
        // Only interested in if the tree along is valid, not its children
        children: [FAKE_ITEM_FOR_VALIDATION],
    };
    const isOwnerStructureValid = beneficialOwnersStructureSchema.isValidSync(itemForValidation);

    const showEditButton = !ownerInfoError && isOwnerStructureValid && !hasErrorInItem;

    // Visual highlight: when "Additional Owner Data Required" toast is shown, we temporarily highlight
    // the "Owner Data Required" buttons that still need missing data.
    const [ownerDataHighlightRequested, setOwnerDataHighlightRequested] = useState(false);
    const ownerDataHighlightTimeoutRef = useRef<number | null>(null);

    useEffect(() => {
        const handleOwnerDataRequired = () => {
            if (depth <= 2) {
                setOwnerDataHighlightRequested(true);

                // Clear any existing timeout so we don't stack them
                if (ownerDataHighlightTimeoutRef.current !== null) {
                    window.clearTimeout(ownerDataHighlightTimeoutRef.current);
                }

                ownerDataHighlightTimeoutRef.current = window.setTimeout(() => {
                    setOwnerDataHighlightRequested(false);
                    ownerDataHighlightTimeoutRef.current = null;
                }, 5000);
            }
        };

        window.addEventListener('ownerDataRequired', handleOwnerDataRequired);

        return () => {
            window.removeEventListener('ownerDataRequired', handleOwnerDataRequired);
            if (ownerDataHighlightTimeoutRef.current !== null) {
                window.clearTimeout(ownerDataHighlightTimeoutRef.current);
            }
        };
    }, [depth]);

    const ownerDataHighlight =
        ownerDataHighlightRequested && !showEditButton && depth <= 2;

    // If a new owner has been added
    if (item.isNewNode)
        return (
            <SingleNewItem
                owner={item}
                fieldname={fieldname}
                parentFieldname={parentFieldname}
                handleDeleteOwner={() => handleDeleteOwner(fieldname, parentFieldname)}
                hideLeftBar={hideLeftBar}
            />
        );
    // Shouldn't happen but a safeguard
    if (!owner) return null;

    const { bIsBusiness } = item;

    const hasSiblingPastCurrent = (currentIndex: number) => {
        const children = item.children ?? [];
        return children.slice(currentIndex + 1).length > 0;
    };

    const hideBranchStyle = { top: height / 2, height: `calc(100% - ${height / 2}px)` };

    const ownPercentageTotal = getDirectOwnersPercentageTotal(item.children);
    const ownPercentageExceeds = bIsBusiness && ownPercentageTotal > 100;

    return (
        <div className="SingleTreeItemWrapper">
            <div
                ref={ref}
                className={`SingleTreeItem ${
                    hasError || ownPercentageExceeds ? 'Error' : ''
                } ${bIsBusiness ? 'Business' : ''}`}
            >
                {hideLeftBar && <div aria-hidden="true" className="FloatingHide" />}
                <div aria-hidden="true" className="FloatingCircle" />
                {bIsBusiness ? (
                    <BusinessIcon />
                ) : (
                    <IndividualIcon
                        style={{ fill: item.bControllingParty ? '#DA0A59' : '#5d52f7' }}
                    />
                )}
                {isBusinessOwner(owner, bIsBusiness) ? (
                    <h4>
                        {owner.entityName} - {item.percentageOwned}%
                    </h4>
                ) : (
                    <h4>
                        {`${owner.firstName} ${owner.lastName}`} - {item.percentageOwned}%
                        {item.bControllingParty ? (
                            <>
                                <br />
                                <span style={{ color: 'gray', fontSize: 10, fontWeight: 500 }}>
                                    Controlling Party
                                </span>
                            </>
                        ) : (
                            ''
                        )}
                    </h4>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {/* Warning icon + highlight while owner-data toast is visible */}
                    <div
                        style={{
                            opacity: !showEditButton && ownerDataHighlight ? 1 : 0,
                            transform: !showEditButton && ownerDataHighlight ? 'scale(1)' : 'scale(0.85)',
                            transition: 'opacity 0.25s ease-in-out, transform 0.25s ease-in-out',
                            width: 20,
                            height: 20,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <WarningIcon
                            style={{
                                width: 20,
                                height: 20,
                                fill: '#DA0A59',
                                flexShrink: 0,
                            }}
                        />
                    </div>

                    <Button
                        className={showEditButton ? 'ValidButton' : 'InvalidButton'}
                        style={{
                            width: '220px',
                        }}
                        onClick={() => handleUpdateOwner(fieldname)}
                        type="button"
                        color="third"
                    >
                        {showEditButton ? 'Edit Owners Data' : 'Owner Data Required'}
                    </Button>
                </div>
                <button
                    type="button"
                    onClick={() => handleDeleteOwner(fieldname, parentFieldname)}
                    className="DeleteIconButton"
                    title="Delete owner"
                >
                    <DeleteIcon className="DeleteIcon" />
                </button>
            </div>
            <div className="Relative">
                {isMainInstance && bIsBusiness && depth < MAX_DEPTH && (
                    <>
                        <TreeActionButtons
                            {...actionProps}
                            fieldname={fieldname}
                            hideLeftBar={hideLeftBar}
                            hideAddBusiness={depth === MAX_DEPTH - 1}
                            hasError={hasError}
                            hasControllingParty={checkHasControllingParty(item.children || [])}
                            hasSubmitted={hasSubmitted}
                            percentageFull={ownPercentageTotal >= 100}
                        />
                        {(item.children ?? []).length > 0 && (
                            <div style={{
                                fontSize: '12px',
                                fontWeight: 500,
                                marginTop: '4px',
                                marginBottom: '4px',
                                color: ownPercentageExceeds ? '#DA0A59' : '#666',
                            }}>
                                {ownPercentageExceeds
                                    ? `Ownership total: ${parseFloat(ownPercentageTotal.toFixed(3))}% — exceeds 100%`
                                    : `Ownership total: ${parseFloat(ownPercentageTotal.toFixed(3))}% — ${parseFloat(Math.max(0, 100 - ownPercentageTotal).toFixed(3))}% remaining`}
                            </div>
                        )}
                    </>
                )}
                {hasError && (
                    <ErrorM
                        key={
                            !!item.children && item.children.length > 0 ? 'ErrorWithChild' : 'Error'
                        }
                        className={`TreeItemError ${
                            !!item.children && item.children.length > 0 ? 'HasChildren' : ''
                        }`}
                        name={fieldname}
                    />
                )}
            </div>
            {item.children && item.children.length > 0 && (
                <div className="ChildrenWrapper">
                    <div className="ConnectingLine" />
                    <div className={`BranchWrapper ${item.ownersGuid}`}>
                        {!hideBranchLink && (
                            <div
                                aria-hidden="true"
                                style={hideBranchStyle}
                                className="FloatingHide Full Branch"
                            />
                        )}
                        {item.children.map((child, i) => (
                            <SingleTreeItem
                                hasControllingParty={checkHasControllingParty(item.children || [])}
                                checkHasControllingParty={checkHasControllingParty}
                                key={i}
                                depth={depth + 1}
                                item={child}
                                handleDeleteOwner={handleDeleteOwner}
                                fieldname={`${fieldname}.children[${i}]`}
                                lookupOwner={lookupOwner}
                                hideLeftBar={i === (item.children ?? []).length - 1}
                                hideBranchLink={hasSiblingPastCurrent(i)}
                                handleUpdateOwner={handleUpdateOwner}
                                {...actionProps}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
